<template>
  <div class="page-wrap">

    <!-- Delete confirmation modal -->
    <transition name="modal-fade">
      <div class="del-overlay" v-if="deleteModal.show" @click.self="deleteModal.show = false">
        <div class="del-modal">
          <div class="del-modal__icon">
            <i class="fa fa-trash"></i>
          </div>
          <div class="del-modal__title">Delete Promocode?</div>
          <div class="del-modal__body">
            This action cannot be undone. The promocode will be permanently removed.
          </div>
          <div class="del-modal__actions">
            <button class="del-modal__cancel" @click="deleteModal.show = false">Cancel</button>
            <button class="del-modal__confirm" @click="confirmDelete">Delete</button>
          </div>
        </div>
      </div>
    </transition>

    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon">
          <i class="fa fa-percent"></i>
        </div>
        <div class="panel-header__text">
          <div class="panel-header__title">Promocodes</div>
          <div class="panel-header__sub">Manage discount and promo codes</div>
        </div>
        <div class="panel-header__action" v-if="currentUser.u.roles == 'admin'">
          <router-link :to="{ name: 'client.promocode.create' }">
            <button class="btn-add">
              <i class="fa fa-plus"></i> Add Promocode
            </button>
          </router-link>
        </div>
      </div>

      <!-- Filter bar -->
      <div class="filter-bar">
        <div class="filter-bar__field">
          <i class="fa fa-search filter-bar__icon"></i>
          <input
            type="text"
            class="filter-bar__input"
            placeholder="Search by promocode or company name…"
            v-model="searchInput"
          />
        </div>
      </div>

      <!-- Loading -->
      <div class="tbl-loading" v-if="loadingData">
        <div class="tbl-loading__spinner"></div>
        <span>Loading promocodes…</span>
      </div>

      <!-- Table -->
      <div class="table-wrap" v-if="!loadingData && filteredPromoCodes.length > 0" @click="activeMenu = null">
        <table class="promo-table">
          <thead>
            <tr>
              <th class="th-num">#</th>
              <th>Title</th>
              <th>Company Name</th>
              <th>Code</th>
              <th>Valid Date</th>
              <th class="th-center">Discount</th>
              <th class="th-center">Status</th>
              <th v-if="currentUser.u.roles == 'admin'" class="th-action"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in filteredPromoCodes" :key="index" class="promo-row">
              <td class="td-num">{{ index + 1 }}</td>
              <td>{{ item.title }}</td>
              <td>{{ item.company_name }}</td>
              <td>
                <span class="code-badge">{{ item.promo_code }}</span>
              </td>
              <td class="td-dates">
                <span>{{ item.valid_from | convertDateFormat }}</span>
                <span class="date-sep">–</span>
                <span>{{ item.valid_to | convertDateFormat }}</span>
              </td>
              <td class="td-center">
                <span class="discount-badge">{{ item.discount_percentage }}%</span>
              </td>
              <td class="td-center">
                <span class="status-badge status-badge--active" v-if="item.is_active">Active</span>
                <span class="status-badge status-badge--inactive" v-else>Inactive</span>
              </td>
              <td class="td-action" v-if="currentUser.u.roles == 'admin'">
                <div class="dot-menu" :class="{ 'dot-menu--open': activeMenu === index }">
                  <button class="dot-menu__trigger" @click.stop="toggleMenu(index)">
                    <i class="fa fa-ellipsis-v"></i>
                  </button>
                  <div class="dot-menu__dropdown" v-if="activeMenu === index">
                    <button class="dot-menu__item" @click="editData(item.id); activeMenu = null">
                      <i class="fa fa-pen"></i> Edit
                    </button>
                    <button class="dot-menu__item dot-menu__item--danger" @click="deleteItem(item.id, index)">
                      <i class="fa fa-trash"></i> Delete
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty state -->
      <div class="empty-state" v-if="!loadingData && filteredPromoCodes.length < 1">
        <i class="fa fa-percent empty-state__icon"></i>
        <div class="empty-state__title">No promocodes found</div>
        <div class="empty-state__sub">Try a different search or add a new promocode</div>
      </div>

    </div>
  </div>
</template>

<script>
import moment from "moment";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import {
  FETCH_PROMOCODES,
  DELETE_PROMOCODE
} from "@/core/services/store/actions.type";
import { mapGetters } from "vuex";
export default {
  data() {
    return {
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Promocodes", route: "" }
      ],
      loadingData: false,
      searchInput: "",
      activeMenu: null,
      deleteModal: { show: false, id: null, index: null }
    };
  },
  computed: {
    ...mapGetters(["getPromocodes", "currentUser"]),
    filteredPromoCodes() {
      let tempPromocode = this.getPromocodes;
      if (this.searchInput != "" && this.searchInput) {
        tempPromocode = tempPromocode.filter(item => {
          return (
            item.promo_code.toUpperCase().includes(this.searchInput.toUpperCase()) ||
            item.company_name.toUpperCase().includes(this.searchInput.toUpperCase())
          );
        });
      }
      return tempPromocode;
    }
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.getPromocodesData();
  },
  mounted() {
    this._closeMenu = () => { this.activeMenu = null; };
    document.addEventListener("click", this._closeMenu);
  },
  beforeDestroy() {
    document.removeEventListener("click", this._closeMenu);
  },
  filters: {
    convertDateFormat(value) {
      return moment(value).format("MM/DD/YYYY");
    }
  },
  methods: {
    toggleMenu(index) {
      this.activeMenu = this.activeMenu === index ? null : index;
    },
    deleteItem(id, index) {
      this.activeMenu = null;
      this.deleteModal = { show: true, id, index };
    },
    confirmDelete() {
      const { id, index } = this.deleteModal;
      this.deleteModal.show = false;
      this.$store.dispatch(DELETE_PROMOCODE, id).then(data => {
        this.$toastr.s(data.msg);
        this.getPromocodes.splice(index, 1);
      });
    },
    editData(id) {
      this.$router.push({ name: "client.promocode.edit", params: { id: id } });
    },
    getPromocodesData() {
      this.loadingData = true;
      this.$store
        .dispatch(FETCH_PROMOCODES)
        .then(() => { this.loadingData = false; })
        .catch(() => { this.loadingData = false; });
    }
  }
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$green:  #276749;
$border: #dde2ec;

@keyframes spin { to { transform: rotate(360deg); } }

.page-wrap {
  padding: 12px;
  font-family: $font;
}

/* ── panel card ── */
.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

/* ── header ── */
.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    flex-shrink: 0;
  }

  &__text { flex: 1; }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $dark;
    line-height: 1.2;
  }

  &__sub {
    font-size: 12px;
    color: #7a8499;
    margin-top: 3px;
  }

  &__action { flex-shrink: 0; }
}

.btn-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: $blue;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;

  &:hover {
    background: #2d85bb;
    box-shadow: 0 4px 12px rgba(56,154,212,0.3);
  }
}

/* ── filter bar ── */
.filter-bar {
  display: flex;
  align-items: center;
  padding: 14px 24px;
  border-bottom: 1px solid $border;

  &__field {
    position: relative;
    flex: 1;
    max-width: 420px;
  }

  &__icon {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    color: #a0aab8;
    font-size: 12px;
    pointer-events: none;
  }

  &__input {
    width: 100%;
    height: 38px;
    border: 1.5px solid $border;
    border-radius: 8px;
    padding: 0 12px 0 32px;
    font-size: 13px;
    font-family: $font;
    color: $dark;
    background: #fafbfd;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;

    &::placeholder { color: #b0bac9; }

    &:focus {
      border-color: $blue;
      box-shadow: 0 0 0 3px rgba(56,154,212,0.12);
      background: #fff;
    }
  }
}

/* ── loading ── */
.tbl-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 28px 24px;
  font-size: 13px;
  color: #7a8499;

  &__spinner {
    width: 18px;
    height: 18px;
    border: 2.5px solid #c5d8f5;
    border-top-color: $blue;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    flex-shrink: 0;
  }
}

/* ── table ── */
.table-wrap {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 560px;
  padding: 0 24px 24px;
}

.promo-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: $dark;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 10px 14px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #7a8499;
    border-bottom: 2px solid $border;
    white-space: nowrap;
    background: #fafbfd;
  }

  .th-num    { width: 44px; text-align: center; }
  .th-center { text-align: center; }
  .th-action { width: 44px; }

  td {
    padding: 11px 14px;
    border-bottom: 1px solid #f0f2f8;
    vertical-align: middle;
    white-space: nowrap;
  }

  .promo-row {
    transition: background 0.12s;
    &:hover { background: #f8fbff; }
    &:last-child td { border-bottom: none; }
  }

  .td-num    { text-align: center; color: #a0aab8; font-size: 12px; }
  .td-center { text-align: center; }
  .td-action { text-align: right; padding-right: 12px; }
  .td-dates  { display: flex; align-items: center; gap: 5px; font-size: 12.5px; }
}

.date-sep { color: #b0bac9; }

/* ── code badge ── */
.code-badge {
  display: inline-flex;
  padding: 3px 9px;
  background: rgba(56,154,212,0.1);
  color: $blue;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
  font-family: 'Courier New', monospace;
}

/* ── discount badge ── */
.discount-badge {
  display: inline-flex;
  padding: 3px 9px;
  background: rgba(39,103,73,0.1);
  color: $green;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

/* ── status badges ── */
.status-badge {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;

  &--active   { background: #e6f4ec; color: $green; }
  &--inactive { background: #f4f5f8; color: #9aa0b0; }
}

/* ── 3-dot menu ── */
.dot-menu {
  position: relative;
  display: inline-flex;
  justify-content: flex-end;

  &__trigger {
    width: 30px;
    height: 30px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #a0aab8;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s;

    &:hover { background: #f0f3fa; color: $dark; }
  }

  &--open &__trigger { background: #f0f3fa; color: $dark; }

  &__dropdown {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    background: #fff;
    border: 1.5px solid $border;
    border-radius: 10px;
    box-shadow: 0 6px 20px rgba(0,0,0,0.1);
    z-index: 50;
    min-width: 130px;
    overflow: hidden;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 9px 14px;
    border: none;
    background: transparent;
    font-size: 13px;
    font-family: $font;
    color: $dark;
    cursor: pointer;
    text-align: left;
    transition: background 0.12s;

    &:hover { background: #f5f8ff; }

    i { font-size: 12px; color: #7a8499; width: 14px; }

    &--danger {
      color: #b42318;
      i { color: #b42318; }
      &:hover { background: #fff5f5; }
    }
  }
}

/* ── delete modal ── */
.del-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13,27,62,0.45);
  backdrop-filter: blur(4px);
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.del-modal {
  background: #fff;
  border-radius: 18px;
  padding: 36px 32px 28px;
  width: 100%;
  max-width: 380px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0,0,0,0.18);

  &__icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #fdecea;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 18px;
    font-size: 20px;
    color: #b42318;
  }

  &__title {
    font-size: 17px;
    font-weight: 700;
    color: $dark;
    margin-bottom: 10px;
  }

  &__body {
    font-size: 13px;
    color: #7a8499;
    line-height: 1.6;
    margin-bottom: 28px;
  }

  &__actions {
    display: flex;
    gap: 10px;
  }

  &__cancel {
    flex: 1;
    height: 40px;
    background: #f0f3fa;
    color: $dark;
    border: 1.5px solid $border;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    font-family: $font;
    cursor: pointer;
    transition: background 0.15s;

    &:hover { background: #e4eaf5; }
  }

  &__confirm {
    flex: 1;
    height: 40px;
    background: #b42318;
    color: #fff;
    border: none;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    font-family: $font;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;

    &:hover {
      background: #981d14;
      box-shadow: 0 4px 14px rgba(180,35,24,0.35);
    }
  }
}

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.2s; }
.modal-fade-enter-active .del-modal, .modal-fade-leave-active .del-modal {
  transition: transform 0.2s, opacity 0.2s;
}
.modal-fade-enter .del-modal, .modal-fade-leave-to .del-modal {
  transform: scale(0.94) translateY(8px);
  opacity: 0;
}
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }

/* ── empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px;
  gap: 10px;

  &__icon { font-size: 38px; color: #c5d0e0; }
  &__title { font-size: 15px; font-weight: 600; color: $dark; }
  &__sub   { font-size: 13px; color: #7a8499; }
}
</style>
